import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import {
    MNOChannel,
    PaymentProvider,
    PixApiProvider,
    TranslationScopes,
} from "@finbackoffice/enums";
import { IDepositRemainderResponse, ISiteConfigs } from "@finbackoffice/clientbff-client";
import { ConfigContextV2, ExchangeRatesContextV2 } from "contexts";
import { useTranslation } from "react-i18next";
import {
    ClientBFFContext,
    getCurrencyConfig,
    TolaWalletDataContext,
    round,
    formatAmount,
    convertToCurrencyFormatterConfig,
} from "@finbackoffice/site-core";

export const useFirstStepDepositTolaV2 = ({
    depositRemainderResponse,
    mnoChannel,
}: {
    depositRemainderResponse: IDepositRemainderResponse | undefined;
    mnoChannel?: MNOChannel;
}) => {
    const { t } = useTranslation(TranslationScopes.TolaWallet);
    const client = useContext(ClientBFFContext);
    const { siteConfigs, siteDefaultCurrency, sitePaymentsCurrencyConfig } =
        useContext(ConfigContextV2);
    const { exchangeRates: rates } = useContext(ExchangeRatesContextV2);
    const currencyConfig = getCurrencyConfig(siteConfigs as ISiteConfigs, siteDefaultCurrency);
    const paymentsCurrencyConfig = sitePaymentsCurrencyConfig?.[siteDefaultCurrency];
    const { min, max_usd } = currencyConfig.deposit || {};
    const { setDepositStep, setDepositData } = useContext(TolaWalletDataContext);
    const [value, setValue] = useState<string>(min ? `${min}` : "");
    const [error, setError] = useState<string>("");
    const [errorApi, setErrorApi] = useState<string>("");
    const [exceededLimit, setExceededLimit] = useState<string>("");
    const [loading, setLoading] = useState(false);
    const currentCurrencyRate = rates?.[siteDefaultCurrency]?.find(
        (r) => r.toCurrency === "USD",
    )?.rate;
    const max = currentCurrencyRate && max_usd ? max_usd / Number(currentCurrencyRate) : 0;
    const roundedMax = round(max, currencyConfig.digits);
    const userPeriodLimits = depositRemainderResponse?.max_period_limits?.[0];
    const userDepositLimit = userPeriodLimits
        ? round(
              Number(userPeriodLimits.amount) - Number(userPeriodLimits.actual_sum),
              currencyConfig.digits,
          )
        : undefined;

    const maxLimit = useMemo(() => {
        if (typeof userDepositLimit !== "number") return roundedMax;
        return roundedMax > userDepositLimit ? userDepositLimit : roundedMax;
    }, [roundedMax, userDepositLimit]);

    const isResponsibleLimit = useMemo(() => {
        if (typeof userDepositLimit !== "number") return false;
        return userDepositLimit < roundedMax;
    }, [roundedMax, userDepositLimit]);

    const depositProviders = useMemo(() => {
        const providersData = (siteConfigs as ISiteConfigs).payments.PixAPI?.deposit || {};
        return (Object.keys(providersData) as PixApiProvider[])
            .map((provider) => ({
                provider,
                ...providersData[provider]!,
            }))
            .filter((p) => p.enabled)
            .sort((a, b) => a.order - b.order);
    }, [siteConfigs]);

    const [selectedOperator, setSelectedOperator] = useState<MNOChannel | undefined>(mnoChannel);

    useEffect(() => {
        setErrorApi("");
    }, [value, selectedOperator]);

    useEffect(() => {
        const numberValue = Number(value || 0);
        if (!value) {
            setError("");
            return;
        }
        if (numberValue < (min ?? 0) || numberValue > maxLimit) {
            setError(
                t("min_max_validation", {
                    min: formatAmount({
                        format:
                            (paymentsCurrencyConfig &&
                                convertToCurrencyFormatterConfig(
                                    siteDefaultCurrency,
                                    paymentsCurrencyConfig,
                                )) ??
                            null,
                        amount: (min ?? 0).toString(),
                        withCode: false,
                    }),
                    max: formatAmount({
                        format:
                            (paymentsCurrencyConfig &&
                                convertToCurrencyFormatterConfig(
                                    siteDefaultCurrency,
                                    paymentsCurrencyConfig,
                                )) ??
                            null,
                        amount: maxLimit.toString(),
                        withCode: false,
                    }),
                }),
            );
        } else {
            setError("");
        }
    }, [value, min, maxLimit, t, paymentsCurrencyConfig, siteDefaultCurrency]);

    const clickDeposit = useCallback(async () => {
        if (!selectedOperator) return;
        setErrorApi("");
        setExceededLimit("");
        try {
            setLoading(true);
            const response = await client.initiateDeposit({
                provider: PaymentProvider.TolaAPI,
                currency: siteDefaultCurrency,
                amount: value,
                provider_data: {
                    channel: selectedOperator,
                },
            });
            setDepositData(response.deposit ?? null);
            setDepositStep("complete");
        } catch (e: any) {
            const errors = e.response.data?.details ?? {};
            if (errors.max_24h_count_deposit_error) {
                setExceededLimit(errors.max_24h_count_deposit_error);
                return;
            }
            if (errors.error_message) {
                setErrorApi(errors.error_message);
                return;
            }
            setErrorApi(e.response.data?.message || e.message);
        } finally {
            setLoading(false);
        }
    }, [selectedOperator, client, siteDefaultCurrency, value, setDepositData, setDepositStep]);

    return useMemo(
        () => ({
            clickDeposit,
            value,
            error,
            loading,
            min,
            maxLimit,
            currencyConfig,
            setValue,
            errorApi,
            exceededLimit,
            depositProviders,
            selectedOperator,
            isResponsibleLimit,
            setSelectedOperator,
        }),
        [
            clickDeposit,
            value,
            error,
            loading,
            setValue,
            min,
            maxLimit,
            currencyConfig,
            errorApi,
            exceededLimit,
            depositProviders,
            selectedOperator,
            isResponsibleLimit,
            setSelectedOperator,
        ],
    );
};
